import { use, useMemo } from "react";
import { cn } from "@/lib/utils";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { type Rename, renamesOf } from "@/lib/old-names";
import { TerminologyContext } from "../terms/Terminology";
import { OldNameNotes } from "../figures/OldNameNotes";
import { Panel } from "@/components/ui/Panel";
import { Link } from "react-router";
import type {
  FigureDefinitionModel,
  FigureListResponse,
  FigureResponse,
  FigureReviewState,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { numeralsOf } from "@/lib/numbering";
import { useFigureDrawing } from "@/hooks/use-figure-drawing";
import { LoadFailure } from "@/components/shared/LoadFailure";
import {
  useConfirmsOnTheirWay,
  useFigureReview,
} from "@/hooks/use-figure-review";
import { FigurePreview } from "./FigurePreview";
import { ReviewSection } from "./ReviewSection";

/**
 * The confirmable all-figures section of the review screen.
 *
 * Everything the attorney is signing off per figure sits on one card: the
 * formal rendered drawing, the captured walk-through (read only — it is edited
 * on Define), the titled components, and that figure's own Confirm. The
 * section wears Reviewed only once every placed figure's yes stands, which the
 * server projects; nothing here decides validity.
 */

/** One titled component as the card lists it: numeral first, then the title. */
interface ComponentRow {
  numeral: string;
  title: string;
}

/**
 * Numeral order as a reader counts: 12 before 12a before 12b before 14. A plain string
 * compare would put 100 before 12, and a number compare cannot read a letter at all.
 */
const byNumeral = new Intl.Collator("en", { numeric: true });

function componentRows(
  definition: FigureDefinitionModel | undefined,
  numerals: ReadonlyMap<string, string>,
  titles: ReadonlyMap<string, string>,
): ComponentRow[] {
  if (definition === undefined || !definition.numbered) return [];
  const pinned = new Set(
    (definition.instances ?? []).map((instance) => instance.element_id),
  );
  return [...pinned]
    .map((elementId) => ({
      numeral: numerals.get(elementId),
      title: titles.get(elementId)?.trim() ?? "",
    }))
    .filter(
      (row): row is ComponentRow =>
        row.numeral !== undefined && row.title !== "",
    )
    .sort((a, b) => byNumeral.compare(a.numeral, b.numeral));
}

/** The figure's formal drawing, fetched by name so no client ever holds a key. */
function RenderedDrawing({
  taskId,
  figure,
}: {
  taskId: string;
  figure: FigureResponse;
}) {
  const { url, error, retry } = useFigureDrawing(taskId, figure.file_id);

  if (url === undefined && error !== null) {
    return (
      <LoadFailure
        error={error}
        fallback="The drawing could not be prepared."
        onRetry={retry}
      />
    );
  }
  if (url === undefined) {
    return (
      <div className="grid aspect-[4/3] place-items-center rounded border border-border bg-muted text-xs text-muted-foreground">
        Preparing drawing…
      </div>
    );
  }
  // The dialog, not an overlay of this section's own. It was a bare div that
  // closed on a click and listened for nothing else, so a keyboard that opened
  // it had no way back out.
  return (
    <FigurePreview figure={figure} drawingUrl={url}>
      <img
        src={url}
        alt={`Formal drawing of figure ${figure.label ?? ""}`}
        className="mx-auto max-h-72 w-auto max-w-full rounded border border-border p-2"
      />
    </FigurePreview>
  );
}

function ComponentList({ rows }: { rows: ComponentRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="mt-3 text-xs font-medium text-muted-foreground">
        No named components.
      </p>
    );
  }
  return (
    <div className="mt-3 flex flex-col gap-1.5 border-t border-border pt-3">
      {rows.map((row) => (
        <div
          key={row.numeral}
          data-testid="summary-component"
          className="flex items-center gap-2 text-sm font-medium"
        >
          <span className="inline-flex h-5 min-w-6 items-center justify-center rounded-full border border-border bg-muted px-1.5 text-[11px] font-semibold">
            {row.numeral}
          </span>
          {row.title}
        </div>
      ))}
    </div>
  );
}

type ReviewEntry = NonNullable<FigureReviewState["figures"]>[string];

/** The way back to Define, and this figure's own yes as it stands. */
function CardActions({
  taskId,
  figure,
  entry,
  onConfirm,
  confirmPending,
}: {
  taskId: string;
  figure: FigureResponse;
  entry: ReviewEntry | undefined;
  onConfirm: (fileId: string, fingerprint: string) => void;
  confirmPending: boolean;
}) {
  return (
    <div className="mt-3 flex items-center gap-2">
      <Link
        to={`/drafts/${taskId}/figures/define?figure=${figure.file_id}`}
        className="text-xs font-semibold text-primary underline-offset-2 hover:underline"
      >
        ✎ Edit Figure
      </Link>
      {entry !== undefined && !entry.confirmed && (
        <Button
          size="dense"
          variant="secondary"
          className="ml-auto"
          disabled={confirmPending}
          onClick={() => onConfirm(figure.file_id, entry.fingerprint)}
        >
          ✓ Confirm
        </Button>
      )}
      {entry?.confirmed === true && (
        <span className="ml-auto text-xs font-bold text-success-strong">
          ✓ Confirmed
        </span>
      )}
    </div>
  );
}

/** Words captured on Define, read only here; `missing` says so when there are none. */
function Captured({ text, missing }: { text: string; missing: string }) {
  if (text === "") {
    return (
      <p className="mt-1 text-sm font-medium text-destructive">{missing}</p>
    );
  }
  return (
    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{text}</p>
  );
}

function SummaryCard({
  taskId,
  figure,
  definition,
  rows,
  entry,
  onConfirm,
  confirmPending,
  renames,
}: {
  taskId: string;
  figure: FigureResponse;
  definition: FigureDefinitionModel | undefined;
  rows: ComponentRow[];
  entry: ReviewEntry | undefined;
  onConfirm: (fileId: string, fingerprint: string) => void;
  confirmPending: boolean;
  renames: Rename[];
}) {
  return (
    <Panel
      as="figure"
      data-testid={`figure-summary-${figure.file_id}`}
      className="m-0 grid grid-cols-[minmax(240px,42%)_1fr] items-start gap-4 bg-transparent p-3 text-left"
    >
      <div>
        <RenderedDrawing taskId={taskId} figure={figure} />
        <figcaption className="mt-2 text-sm font-semibold">
          Figure {figure.label}{" "}
          <span className="font-mono text-[11px] font-normal text-muted-foreground">
            ({figure.original_filename})
          </span>
        </figcaption>
      </div>
      <div>
        <MicroLabel as="p" size="sm">
          Brief Figure Description
        </MicroLabel>
        <Captured
          text={definition?.description.trim() ?? ""}
          missing="No brief description yet."
        />
        <MicroLabel as="p" size="sm" className="mt-3">
          Figure walkthrough
        </MicroLabel>
        <Captured
          text={definition?.walkthrough.trim() ?? ""}
          missing="Not captured yet."
        />
        <OldNameNotes text={definition?.walkthrough ?? ""} renames={renames} />
        <ComponentList rows={rows} />
        <CardActions
          taskId={taskId}
          figure={figure}
          entry={entry}
          onConfirm={onConfirm}
          confirmPending={confirmPending}
        />
      </div>
    </Panel>
  );
}

/**
 * The three lookups a summary card reads, all derived from one definitions set.
 *
 * Memoised together because they turn over together: a new definitions set
 * replaces all three, and nothing else changes any of them.
 */
function useDefinitionLookups(definitions: FigureListResponse["definitions"]) {
  const numerals = useMemo(
    () =>
      numeralsOf({
        registry: definitions?.registry ?? [],
        settings: definitions?.settings,
      }),
    [definitions],
  );
  const titles = useMemo(
    () =>
      new Map(
        (definitions?.registry ?? []).map((row) => [row.element_id, row.title]),
      ),
    [definitions],
  );
  const definitionsById = useMemo(
    () => new Map((definitions?.figures ?? []).map((d) => [d.file_id, d])),
    [definitions],
  );
  return { numerals, titles, definitionsById };
}

export function FigureSummaries({
  taskId,
  data,
}: {
  taskId: string;
  data: FigureListResponse;
}) {
  const review = useFigureReview(taskId);
  const onTheirWay = useConfirmsOnTheirWay(taskId);
  const renames = renamesOf(use(TerminologyContext)?.view);
  const byId = new Map((data.figures ?? []).map((f) => [f.file_id, f]));
  const entries = data.layout?.entries ?? [];
  // The server projects the review for placed figures only, so its entries are
  // the section's own predicate; re-walking the layout here would be a second
  // implementation of "placed" free to disagree with the gate.
  const reviewEntries = Object.values(data.review?.figures ?? {});
  // With no figures placed there is nothing to confirm here, and the figures
  // gate opens on the arrangement's yes alone, so the tag reads that yes too.
  const allConfirmed =
    reviewEntries.length > 0
      ? reviewEntries.every((entry) => entry.confirmed)
      : data.review?.layout_confirmed === true;

  const { numerals, titles, definitionsById } = useDefinitionLookups(
    data.definitions,
  );
  const confirm = (fileId: string, fingerprint: string) =>
    review.mutate({ op: "confirm_figure", file_id: fileId, fingerprint });

  const card = (fileId: string) => {
    const figure = byId.get(fileId);
    if (figure === undefined) return null;
    const definition = definitionsById.get(fileId);
    return (
      <SummaryCard
        key={fileId}
        taskId={taskId}
        figure={figure}
        definition={definition}
        rows={componentRows(definition, numerals, titles)}
        entry={data.review?.figures?.[fileId]}
        onConfirm={confirm}
        confirmPending={onTheirWay.has(fileId)}
        renames={renames}
      />
    );
  };

  return (
    <ReviewSection
      title="Figure summaries"
      reviewed={allConfirmed}
      tagTestId="summaries-tag"
    >
      <p className="mt-2 text-xs text-muted-foreground">
        Each figure with its walk-through and component list. Confirm each one,
        and use Edit Figure to make changes.
      </p>
      <div className="mt-3 flex flex-col gap-3 pb-2">
        {entries.map((entry, index) =>
          entry.kind === "figure" ? (
            card(entry.file_id)
          ) : (
            <Panel
              key={entry.group_id}
              pad="tight"
              data-testid={`summary-group-${entry.group_id}`}
              className={cn(
                "border-dashed p-3",
                index % 2 === 0
                  ? "border-primary/40 bg-primary/5"
                  : "border-info/40 bg-info/5",
              )}
            >
              <MicroLabel as="p" className="mb-2 text-current">
                {entry.title}
              </MicroLabel>
              <div className="flex flex-col gap-3">
                {(entry.members ?? []).map((m) => card(m.file_id))}
              </div>
            </Panel>
          ),
        )}
      </div>
    </ReviewSection>
  );
}
